/**
 * TableSurfaceContext - the batch table's binding of the shared
 * detail-surface context.
 *
 * The machinery (an optional context, a typed API, the selection shape) is in
 * `shared/surface/DetailSurfaceContext`. What is particular to tables is the
 * types below: which pane ids exist, and what a selection identifies.
 *
 * IT IS OPTIONAL ON PURPOSE. `useTableSurface()` returns null outside the
 * provider, so a component rendered anywhere else keeps working.
 *
 * THE SELECTION IS A CELL, AND IT IS AN ADDRESS, NOT A SNAPSHOT.
 *
 * The page used to hold `{ row, column, cell }` OBJECTS in state, which meant
 * the open cell detail was a photograph of the moment it was clicked: a live
 * `cell_completed` event over PubSub rewrote the row in Redux and the pane
 * went on showing the old value. A `DetailSelection` carries an id, so the id
 * is the address `<rowId>:<columnId>` and the page looks the three records up
 * out of the live table on every render. Same open pane, current data.
 */

import {
  DetailSelection,
  DetailSurfaceApi,
  createDetailSurfaceContext,
} from '../../shared/surface/DetailSurfaceContext';
import { TablePanelId } from './layout';

/** The only kind of thing a table's detail pane ever shows. */
export type TableItemKind = 'cell';

export type TableSelection = DetailSelection<TableItemKind>;

/**
 * `never` for the reader: a table has no reader pane, so `openReader` cannot
 * be typechecked at any call site - which is the truth, there is nowhere for
 * the content to go. Documents open in the global side panel instead.
 */
export type TableSurfaceApi = DetailSurfaceApi<TablePanelId, TableItemKind, never>;

/** The detail pane's address for one cell. */
export const cellSelectionId = (rowId: number, columnId: number): string =>
  `${rowId}:${columnId}`;

/** …and back. Returns null for anything that is not a cell address. */
export const parseCellSelectionId = (
  id: string | null | undefined
): { rowId: number; columnId: number } | null => {
  if (!id) return null;
  const [rawRow, rawColumn] = id.split(':');
  const rowId = Number(rawRow);
  const columnId = Number(rawColumn);
  if (!Number.isFinite(rowId) || !Number.isFinite(columnId)) return null;
  return { rowId, columnId };
};

const binding = createDetailSurfaceContext<TableSurfaceApi>();

export const TableSurfaceProvider = binding.Provider;

/** Null outside a table surface - callers fall back to their old behaviour. */
export const useTableSurface = binding.useSurface;

export default binding.Context;
